/*---------------------------------------------------------*\
 * Imports
\*---------------------------------------------------------*/
import angular from 'angular';
import PubSub from 'pubsub-js';
import keyboard from 'keycode-ui';
import _ from 'lodash';

const deps = {};

/*---------------------------------------------------------*\
 * HomeController
\*---------------------------------------------------------*/
class HomeController {
  //-----------------------------------
  // Constructor
  //-----------------------------------
  constructor($scope, $rootScope, $state, $timeout, $stateParams, topics, navigationService, newsService,
              closingsAndDelaysService, localService, storageKeys, states, adProps, $document) {
    deps.$state = $state;
    deps.$timeout = $timeout;
    deps.topics = topics;
    deps.navigationService = navigationService;
    deps.newsService = newsService;
    deps.states = states;
    deps.localService = localService;
    deps.storageKeys = storageKeys;
    deps.$stateParams = $stateParams;
    deps.$rootScope = $rootScope;
    deps.adProps = adProps;
    deps.$document = $document[0];

    this.name = 'home';
    this.showClosingsButton = false;
    this.headerConfig = {};
    this.currentRegion = localService.get(storageKeys.selectedRegion);
    this.storyPageSize = 3;
    this.stories = [];

    const token1 = PubSub.subscribe(deps.topics.headerMain.menuReady, (msg, data) => {
      this.onMenuReady(data);
    });
    const token2 = PubSub.subscribe(deps.topics.headerMain.selectTab, (msg, data) => {
      this.onTabSelect(data.item, data.previousItem);
    });
    const token3 = PubSub.subscribe(deps.topics.headerMain.selectSection, (msg, item) => {
      this.onSectionSelect(item);
    });

    const token4 = PubSub.subscribe(deps.topics.state.reactivate, () => {
      if (deps.$rootScope.previousActiveElement) {
        // Restore focus to previously focused dom element
        $timeout(() => {
          if (this.isFocusLost(deps.$rootScope.previousActiveElement)) {
            deps.navigationService.focusElAsync('.main-content');
          } else {
            deps.$rootScope.previousActiveElement.focus();
          }
          delete deps.$rootScope.previousActiveElement;
        });
      }
    });

    $scope.$on('$destroy', () => {
      PubSub.unsubscribe(token1);
      PubSub.unsubscribe(token2);
      PubSub.unsubscribe(token3);
      PubSub.unsubscribe(token4);
    });

    closingsAndDelaysService.hasClosings(this.region).then((data) => {
      this.showClosingsButton = data;
    });
    this.showClosings = true;
  }

  //-----------------------------------
  // Properties
  //-----------------------------------
  get region() {
    const _region = deps.localService.get(deps.storageKeys.selectedRegion);
    return _region ? _region.abbr : 'LI';
  }

  //-----------------------------------
  // Methods
  //-----------------------------------
  isFocusLost(prevHtmlElement) {
    // if for any reason focus is lost and lands on the body or a subNavItem element was the last item focused
    // before changing state then set focus to the main content section. Mostly used for T&W and forecast video
    // https://tools.emedia.cv.net/jira/browse/NSW-424
    // but can be used for other use cases
    if (angular.isDefined(prevHtmlElement) && (_.contains(prevHtmlElement.tagName.toLowerCase(), 'body') ||
      _.contains(prevHtmlElement.className, 'sub-nav-item'))) {
      deps.navigationService.focusElAsync('.main-content');
    }
  }
  onMenuReady(data) {
    if (data && angular.isObject(data) && !angular.isArray(data)) {
      this.activeTab = data.activeTab;
      this.activeSection = this.activeTab && angular.isArray(data.activeTab.sections) ?
        data.activeTab.sections[0] :
        null;
      if (this.activeSection) {
        this.updateStories().then(() => {
          deps.$timeout(() => {
            deps.navigationService.focusElAsync('.main-content');
          });
        });
      }
    }
  }

  onTabSelect(item, previousItem) {
    if (angular.isObject(item) && !angular.isArray(item)) {
      this.activeTab = item;
      if (item !== previousItem && item.hasStories) {
        // default to first story on first tab select
        this.activeSection = item.sections[0];
        this.updateStories().then(() => {
          if (item.sections.length < 2) {
            // select content if submenu is not active
            deps.navigationService.focusElAsync('.main-content');
          }

          deps.$timeout(() => {
            if (item.sections.length < 2) {
              // select content if submenu is not active
              deps.navigationService.focusElAsync('.main-content');
            }
          });
        });
      }
    }
  }

  onSectionSelect(item) {
    if (item && item.state && item.state.name) {
      if (item.state.params) {
        deps.$state.go(item.state.name, item.state.params);
      } else {
        deps.$state.go(item.state.name);
      }
    } else if (angular.isObject(item) && !angular.isArray(item)) {
      this.activeSection = item;

      if (this.activeSection.type !== 'storylist') {
        deps.$timeout(() => {
          deps.navigationService.focusElAsync('.main-content');
        });
        return;
      }

      this.updateStories().then(() => {
        deps.$timeout(() => {
          deps.navigationService.focusElAsync('.main-content');
        });
      });
    }
  }

  isActiveSection(section) {
    return !_.isUndefined(this.activeSection) && (section === this.activeSection.id ||
      section === this.activeSection.type);
  }

  onClosingsKeyDown(e) {
    const topics = deps.topics.nav;
    let pollCheck;
    switch (true) {
      case keyboard.isUp(e):
        PubSub.publish(topics.up, 'home.closings');
        e.preventDefault();
        break;
      case keyboard.isDown(e):
        pollCheck = !!deps.$document.querySelector('.poll-result');
        PubSub.publish(topics.down, { source: 'home.closings', pollResultVisible: pollCheck });
        e.preventDefault();
        break;
      case keyboard.isLeft(e):
      case keyboard.isRight(e):
        e.preventDefault();
        break;
    }
  }

  onStoryListKeyDown(e) {
    if (keyboard.isUp(e)) {
      this.focusOnNavOrClosings();
      e.preventDefault();
    }
  }

  onPollKeyDown(isEventUp) {
    if (isEventUp) {
      this.focusOnNavOrClosings();
    }
  }

  onOtherRegionKeyDown(e, region, index) {
    if (keyboard.isUp(e)) {
      if (_.inRange(index, 0, 3)) {
        this.focusOnNavOrClosings();
        e.preventDefault();
      }
    } else if (keyboard.isDown(e)) {
      const elems = deps.$document.querySelectorAll('other-regions button');
      const destIndex = elems.length - 1;
      const previousElement = elems[destIndex];
      PubSub.publish(deps.adProps.home.previousElement, { previousElement });
    }
  }

  focusOnNavOrClosings() {
    if (this.showClosingsButton) {
      deps.navigationService.focusEl('.main-content .closings');
    } else {
      deps.navigationService.focusEl('navbar .selected');
    }
  }

  onRegionSelect(region) {
    console.log('## ACTION ## REGION SELECTED', region);
    deps.localService.set(deps.storageKeys.selectedRegion, region);
    deps.$state.go(deps.states.home, null, { reload: true });
    PubSub.publish(deps.topics.regions.select, region);
  }

  onPollExit() {
    deps.$timeout(() => {
      this.activeSection = this.activeTab.sections[0];
      deps.navigationService.focusElAsync('.main-content');
    });
  }

  closingsAndDelays_onSelect() {
    deps.$state.go(deps.states.closingsAndDelays, {});
  }

  onStorySelect(story, focusItem) {
    if (angular.isObject(story) && !angular.isArray(story)) {
      deps.$state.go(deps.states.videoplayer, { category: this.activeTab.id, currentStory: { story, focusItem } });
    }
  }

  onArchiveSelect(story, focusItem) {
    const objCheck = angular.isObject(story) && !angular.isArray(story);
    if (objCheck) {
      deps.$state.go(deps.states.videoplayer, { category: this.activeTab.id, currentStory: { story, focusItem } });
    }
  }

  updateStories() {
    return deps.newsService.getDataFeed(this.region, this.activeSection.id)
      .then((data) => {
        deps.$timeout(() => {
          this.stories = data;
        });
      });
  }
}

HomeController.$inject = [
  '$scope',
  '$rootScope',
  '$state',
  '$timeout',
  '$stateParams',
  'topics',
  'navigationService',
  'newsService',
  'closingsAndDelaysService',
  'localService',
  'storageKeys',
  'states',
  'adProps',
  '$document'
];

export default HomeController;



/** WEBPACK FOOTER **
 ** ./client/app/components/home/home.controller.js
 **/